import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Reorder, useDragControls } from "framer-motion";
import { ChevronDown, ChevronUp, GripVertical, Loader2, Swords } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, posterUrl } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { MovieDetails } from "@/components/MovieDetails";

function RankRow({ movie, index, count, onMove, onDrop, onOpen }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={movie} dragListener={false} dragControls={controls} onDragEnd={onDrop}
      data-testid={`rank-item-${movie.movie_id}`}
      whileDrag={{ scale: 1.03, boxShadow: "0 18px 40px rgba(0,0,0,0.35)", zIndex: 10 }}
      className="relative flex items-center gap-3 p-2.5 rounded-2xl border bg-card select-none">
      <button onPointerDown={(e) => controls.start(e)} aria-label="Drag to reorder" data-testid={`rank-handle-${movie.movie_id}`}
        className="touch-none cursor-grab active:cursor-grabbing p-2 -ml-1 text-muted-foreground hover:text-foreground">
        <GripVertical className="h-5 w-5" />
      </button>
      <span className="w-7 text-center font-display font-black text-lg text-muted-foreground" data-testid={`rank-position-${movie.movie_id}`}>{index + 1}</span>
      <img src={posterUrl(movie.poster_path, "w154")} alt="" draggable={false} className="h-16 w-11 rounded-lg object-cover" />
      <button onClick={() => onOpen(movie)} className="min-w-0 flex-1 text-left" data-testid={`rank-open-${movie.movie_id}`}>
        <p className="font-display font-bold leading-tight truncate">{movie.title}</p>
        <p className="text-xs text-muted-foreground">{movie.year} · {movie.genres.slice(0, 2).join(", ") || "—"}</p>
      </button>
      <div className="flex flex-col">
        <button onClick={() => onMove(index, -1)} disabled={index === 0} aria-label="Move up" data-testid={`rank-up-${movie.movie_id}`}
          className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-25">
          <ChevronUp className="h-4 w-4" />
        </button>
        <button onClick={() => onMove(index, 1)} disabled={index === count - 1} aria-label="Move down" data-testid={`rank-down-${movie.movie_id}`}
          className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-25">
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>
    </Reorder.Item>
  );
}

function useRanking() {
  const [items, setItems] = useState(null);
  const itemsRef = useRef([]);
  const savedKey = useRef("");
  const key = (list) => list.map((m) => m.movie_id).join(",");

  const apply = (list) => { itemsRef.current = list; setItems(list); };

  useEffect(() => {
    api.get("/faceoff/ranking").then(({ data }) => { savedKey.current = key(data.ranking); apply(data.ranking); });
  }, []);

  const save = async () => {
    const list = itemsRef.current;
    if (key(list) === savedKey.current) return;
    try {
      const { data } = await api.put("/ranking", { movie_ids: list.map((m) => m.movie_id) });
      savedKey.current = key(data.ranking);
      apply(data.ranking);
      toast("Ranking saved", { id: "rank-saved", duration: 1200 });
    } catch (e) {
      toast.error(formatApiError(e));
      const { data } = await api.get("/faceoff/ranking");
      savedKey.current = key(data.ranking);
      apply(data.ranking);
    }
  };

  const move = (index, dir) => {
    const list = [...itemsRef.current];
    const to = index + dir;
    if (to < 0 || to >= list.length) return;
    [list[index], list[to]] = [list[to], list[index]];
    apply(list);
    save();
  };
  return { items, apply, save, move };
}

export default function Rank() {
  const { items, apply, save, move } = useRanking();
  const [details, setDetails] = useState(null);

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader eyebrow="Your liked movies" title="My ranking"
        sub="Drag the handle (or use the arrows) to put your favourites in order. Face-offs keep refining it." />
      {!items ? (
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-dashed p-12 text-center text-muted-foreground" data-testid="rank-empty">
          Nothing to rank yet. Swipe up on movies you've seen and liked.
          <Link to="/" className="block mt-4 text-primary font-semibold" data-testid="rank-empty-discover-link">Keep swiping →</Link>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold" data-testid="rank-count">{items.length} liked movies</p>
            <Link to="/faceoff" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary" data-testid="rank-faceoff-link">
              <Swords className="h-4 w-4" /> Face-off
            </Link>
          </div>
          <Reorder.Group axis="y" values={items} onReorder={apply} className="space-y-2.5" data-testid="rank-list">
            {items.map((m, i) => (
              <RankRow key={m.movie_id} movie={m} index={i} count={items.length} onMove={move} onDrop={save} onOpen={setDetails} />
            ))}
          </Reorder.Group>
          <p className="text-xs text-muted-foreground mt-6">Newly liked movies start near the middle. Drag them into place or let face-offs sort them out.</p>
        </>
      )}
      <MovieDetails movie={details} onClose={() => setDetails(null)} />
    </div>
  );
}
